import { z } from 'zod';
import { objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [LIFECYCLE] WHAT ARCHIVING A POST TYPE OR A VEHICLE TYPE WILL DO — STATED BEFORE THE
 *  CLICK, NEVER AFTER. The `deleteImpact` shape, applied to a master.
 *
 *  ⚠️ IT IS NARROWER THAN IT LOOKS, AND SAYING SO IS HALF THE POINT OF THE PAYLOAD.
 *  Only DRAFT arrangements lose the column. A PUBLISHED arrangement is NEVER altered —
 *  no reversion to draft, no column removal, no re-pointing — because [FIX-1] settled
 *  that a frozen requirement is the RECORD of an order that was issued. So this payload
 *  carries `publishedArrangementCount` for one reason: to tell the operator, with a
 *  number rather than a reassurance, that those orders are untouched.
 *
 *  An admin who fears breaking live orders retires nothing, and the master list silts up.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/** One DRAFT arrangement that will lose the column, and what it will lose with it. */
export const masterArchiveDraftSchema = z.object({
  id: objectIdSchema,
  reference: z.string(),
  displayTitle: z.string(),
  paDate: z.string(),
  /**
   * The numbers already typed into that column on this draft — §6b's data loss, per
   * draft. Real data loss inside a draft, and it must not be a surprise.
   */
  typedCount: z.number().int().min(0),
  /**
   * §6c — this draft would be left with NO post column at all and could not then be
   * published. Named for the QUESTION rather than the mechanism, so it reads correctly
   * for a vehicle type too, where it is always `false`: publish's blocker counts people,
   * and a vehicle block alone can never carry an arrangement.
   */
  wouldBeUnpublishable: z.boolean(),
});

export const masterArchivePolicySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  publishedVersionCount: z.number().int().min(0),
  hasDraftVersion: z.boolean(),
});

export const masterArchiveImpactSchema = z.object({
  /** ⚠️ COMPLETE, always. The linked list below is capped; this number never is. */
  draftArrangementCount: z.number().int().min(0),
  draftArrangements: z.array(masterArchiveDraftSchema),
  /**
   * ⚠️ THE CAP IS DISCLOSED RATHER THAN SILENT (CLAUDE.md §7's rule about a bound that
   * under-reports). It is deliberately NOT `getOrgUnitCounts`'s throw-past-the-bound: the
   * COUNT is the load-bearing number and it is exact, while the links are a convenience.
   */
  draftArrangementsTruncated: z.boolean(),
  /** §6b as one number, for the sentence. */
  totalTypedCount: z.number().int().min(0),
  policyCount: z.number().int().min(0),
  policies: z.array(masterArchivePolicySchema),
  /** §6a — returned ONLY in order to be stated as unaffected. */
  publishedArrangementCount: z.number().int().min(0),
  /** §6c — REFERENCES, because the brief says "naming it", not counting it. */
  unpublishableDraftReferences: z.array(z.string()),
  /**
   * ⚠️ ACTIVE standing orders that carry the column, which the cascade deliberately does
   * NOT touch — a standing order is a live, issued thing with no DRAFT state, so it keeps
   * generating a column for an archived master. Nothing breaks (the column still resolves
   * and still fills), but the operator should be told rather than find out.
   */
  standingOrderCount: z.number().int().min(0),
});
